import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, Dialog, Stack, Text, useText } from "@sveabank/cc-common";
import { format } from "date-fns";
import { useState } from "react";
import Paper from "../../components/paper";
import { usePageContext } from "../../hooks/context";
import {
	useFetchClcApplicationRevisionQuery,
	useSetClcOrganisationNumberMutation,
} from "../../services/clc-application";
import type { ClcApplication } from "../../types/clc-application";
import ChooseClcCompanyModal from "./choose-clc-company-modal";
import { useDashboard } from "./hooks";
import NoClcCandidateModal from "./no-clc-candidate-modal";
import { OrgNumberSuccessModal } from "./org-nr-success-modal";

type Props = {
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	pendingClcApplication: ClcApplication;
};

const SetOrgNrModal = ({ isOpen, setOpen, pendingClcApplication }: Props) => {
	const [t, texts] = useText();
	const { possibleClcOrgs } = usePageContext(useDashboard);
	const [setOrgNr] = useSetClcOrganisationNumberMutation();
	useFetchClcApplicationRevisionQuery(pendingClcApplication.applicationId);

	const [chooseClcModal, setChooseClcModal] = useState(false);
	const [successModal, setSuccessModal] = useState(false);

	const connectOrgNr = async (orgNr: string) => {
		await setOrgNr({
			applicationId: pendingClcApplication.applicationId,
			orgNr,
		}).unwrap();
		setSuccessModal(true);
	};

	if (possibleClcOrgs.length === 0)
		return <NoClcCandidateModal isOpen={isOpen} setOpen={setOpen} />;

	return (
		<>
			<Dialog
				isOpen={isOpen}
				size="small"
				center
				heading={t(texts.newCompany.setOrgNumber.heading)}
				icon="warning"
				isDismissable={false}
			>
				<Dialog.Content>
					<Stack gap={4}>
						<Text align="left">{t(texts.newCompany.setOrgNumber.text)}</Text>
						{possibleClcOrgs[0] && (
							<Paper className="bg-background-tertiary">
								<Stack gap={2}>
									<Stack direction="row" justify="between">
										<Text bold>{t(texts.newCompany.setOrgNumber.companyName)}</Text>
										<Text>{possibleClcOrgs[0].name}</Text>
									</Stack>
									<Stack direction="row" justify="between">
										<Text bold>{t(texts.newCompany.setOrgNumber.organisationNumber)}</Text>
										<Text>{possibleClcOrgs[0].orgNrDisplayName}</Text>
									</Stack>
									<Stack direction="row" justify="between">
										<Text bold>{t(texts.newCompany.setOrgNumber.registrationDate)}</Text>
										<Text>{format(possibleClcOrgs[0].registrationDate, "yyyy-MM-dd")}</Text>
									</Stack>
								</Stack>
							</Paper>
						)}
						<Text bold align="left">
							Överensstämmer dessa uppgifter med de uppgifter du angivit i din ansökan om starta
							företag hos oss?
						</Text>
					</Stack>
				</Dialog.Content>
				<Dialog.Footer>
					<ButtonGroup>
						<Button
							variant="primary"
							data-tracking-action="Verify CLC Organisation Number"
							onPress={async () => {
								const org = possibleClcOrgs[0];
								if (!org) throw new Error("No clc org");
								await connectOrgNr(org.orgNr);
								setOpen(false);
							}}
						>
							{t(texts.newCompany.setOrgNumber.primaryButtonText)}
						</Button>
						<Button
							variant="secondary"
							onPress={() => {
								if (possibleClcOrgs.length > 1) setChooseClcModal(true);
								setOpen(false);
							}}
						>
							{t(texts.newCompany.setOrgNumber.secondaryButtonText)}
						</Button>
					</ButtonGroup>
				</Dialog.Footer>
			</Dialog>

			{pendingClcApplication.applicationId && !!possibleClcOrgs.length && chooseClcModal && (
				<ChooseClcCompanyModal
					clcCandidates={possibleClcOrgs}
					isOpen={chooseClcModal}
					setOpen={setChooseClcModal}
					connectOrgNr={connectOrgNr}
				/>
			)}
			<OrgNumberSuccessModal isOpen={successModal} closeModal={() => setSuccessModal(false)} />
		</>
	);
};

export default SetOrgNrModal;
